import "./App.css";
import { ConfigProvider, App as AntdApp } from "antd";
import { Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import GeneralLayout from "./components/GeneralLayout";
import LoginPage from "./pages/auth/LoginPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import AcceptInvitePage from "./pages/auth/AcceptInvitePage";
import JoinPage from "./pages/auth/JoinPage";
import ForcePasswordChangePage from "./pages/auth/ForcePasswordChangePage";
import InvitationExpiredPage from "./pages/auth/InvitationExpiredPage";
import InvitationInvalidPage from "./pages/auth/InvitationInvalidPage";
import ProtectedRoute from "./components/ProtectedRoute";
import UserManagementProtectedRoute from "./components/UserManagementProtectedRoute";
import ModuleProtectedRoute from "./components/ModuleProtectedRoute.jsx";
import WorkflowVisibilityRoute from "./components/WorkflowVisibilityRoute.jsx";
import GlobalKeyAccountsRoute from "./components/GlobalKeyAccountsRoute.jsx";
import PageLoadingFallback from "./components/PageLoadingFallback.jsx";
import { KEY_ACCOUNTS_WORKFLOW_ID, ACCOUNTS_WORKFLOW_ID } from "@dashai/constants/gatedDashboards";
import NotFound from "./components/NotFound";
import UserManagementPage from "./pages/UserManagementPage.jsx";
import SettingsPage from "./pages/SettingsPage";
import LearnHubPage from "./pages/LearnHubPage.jsx";
import { AuthProvider } from "./contexts/AuthContext.jsx";
import { initializeAuth, switchWorkspaceAndUpdateCountry } from "./slices/authSlice";
import { useWorkspaceRevalidation } from "./hooks/useWorkspaceRevalidation";
import { useWorkspaceConfigLoader } from "./hooks/useWorkspaceConfigLoader";
import { setDeepLinkCountry } from "./slices/filtersSlice";
import { useModuleAccess } from "./hooks/useModuleAccess";
import { usePageViews } from "./analytics/usePageViews";
import UpdateAvailableNotification from "./components/UpdateAvailableNotification";
import { NavigationBlockingProvider } from "./contexts/NavigationBlockingContext";
import MaintenanceBanner from "./components/MaintenanceBanner";
import { fetchUserPreferencesFromBackend } from "./slices/userPreferencesSlice";
import {
  fetchPlatformMetadata,
  readPlatformMetadataCache,
  writePlatformMetadataCache,
} from "./api/platformMetadataApi";
import { applyPlatformMetadata } from "./config/platformMapping";
import GlobalCommandPalette from "./components/GlobalCommandPalette";
import FeedbackWidget from "./components/FeedbackWidget/FeedbackWidget";

// Onboarding wizard — lazy so the login path doesn't ship picker deps
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
// DashmoteAI routes — lazy-loaded so analytics-only users don't download AI deps
const DashAIRoutes = lazy(() => import("./features/dashai/DashAIRoutes"));
// Public DashmoteAI routes (no auth required) — shared insight/canvas links
const PublicDashAIRoutes = lazy(() => import("./features/dashai/PublicDashAIRoutes"));

// Dashboard pages: lazy-loaded so first paint doesn't ship every dashboard's
// chart/export deps. Each renders inside GeneralLayout, so the shared fallback
// below shows in the content area while the page's chunk loads.
const HomePage = lazy(() => import("./pages/HomePage"));
const MarketOverviewPage = lazy(() => import("./pages/MarketOverviewPage"));
const MarketDeepdivePage = lazy(() => import("./pages/MarketDeepdivePage"));
const PhysicalLocationPage = lazy(() => import("./pages/PhysicalLocationPage"));
const PhysicalFranchisePage = lazy(() => import("./pages/PhysicalFranchisePage"));
const KeyAccountsEntry = lazy(() => import("./pages/KeyAccountsV2/KeyAccountsEntry"));
const GlobalKeyAccountsPage = lazy(() => import("./pages/GlobalKeyAccountsPage"));
const AccountsPage = lazy(() => import("./pages/AccountsPage"));
const OutletPage = lazy(() => import("./pages/OutletPage"));
const MyListPage = lazy(() => import("./pages/MyListPage"));
const MyListDetails = lazy(() => import("./pages/MyListDetails.jsx"));

const pageSuspenseFallback = <PageLoadingFallback />;

// Param-aware redirects for bare DashAI shortcut paths
function RedirectAutomatedInsights() {
  const { insightId } = useParams();
  return <Navigate to={insightId ? `/ai/automated-insights/${insightId}` : '/ai/automated-insights'} replace />;
}
function RedirectAutomatedReporting() {
  const { reportId } = useParams();
  return <Navigate to={reportId ? `/ai/automated-reporting/${reportId}` : '/ai/automated-reporting'} replace />;
}
function RedirectWorkflowAgents() {
  const { agentId } = useParams();
  return <Navigate to={agentId ? `/ai/workflow-agents/${agentId}` : '/ai/workflow-agents'} replace />;
}
function RedirectChat() {
  const { chatId, canvasId } = useParams();
  return <Navigate to={canvasId ? `/ai/chat/${chatId}/${canvasId}` : `/ai/chat/${chatId}`} replace />;
}
function RedirectSavedCanvas() {
  const { tab } = useParams();
  return <Navigate to={tab ? `/ai/saved-canvas/${tab}` : '/ai/saved-canvas'} replace />;
}

function App() {
  const dispatch = useDispatch();
  // Re-validate a stale persisted workspace selection when a long-open tab is
  // resumed, so it recovers instead of 403-looping on scoped calls.
  useWorkspaceRevalidation();
  // Load the active workspace's presentation config (layout, combo labels,
  // export flag) whenever the session's workspace changes.
  useWorkspaceConfigLoader();
  // Product analytics: emit page_viewed on every authenticated route change and
  // keep the tracker's workspace/market/impersonation context in sync.
  usePageViews();
  const { isInitialized, isAuthenticated, user, userData, userDataFetchedThisSession } = useSelector(
    (state) => state.auth
  );
  const availableWorkspaces = useSelector((state) => state.auth.availableWorkspaces);
  const navigate = useNavigate();
  const { canAccess } = useModuleAccess();
  // Capture refresh-notification email deep-link params at first render, before
  // the root "/" → "/ai" redirect can strip the query string. The CTA carries
  // the refreshed workspace + country so the dashboards open on that market.
  const [emailDeepLink] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const country = p.get("country");
    const idClient = p.get("id_client");
    return country && idClient ? { country, idClient } : null;
  });
  const emailDeepLinkHandledRef = useRef(false);

  // Fetch user_preferences (favorites, switcher visibility, column prefs)
  // once at app root. Page-level hooks also fetch this per workspace; the
  // GET is idempotent.
  const userPrefsFetchedRef = useRef(false);
  useEffect(() => {
    if (
      isAuthenticated &&
      user?.uid &&
      userDataFetchedThisSession &&
      !userPrefsFetchedRef.current
    ) {
      userPrefsFetchedRef.current = true;
      dispatch(fetchUserPreferencesFromBackend());
    }
  }, [isAuthenticated, user?.uid, userDataFetchedThisSession, dispatch]);

  // Refresh-notification email deep-link: switch to the email's workspace and
  // default the dashboards' country to the refreshed market the email is about.
  // Runs once, after auth + the workspace list have resolved.
  useEffect(() => {
    if (emailDeepLinkHandledRef.current || !emailDeepLink) return;
    // Gate on userDataFetchedThisSession (not userData) so we run AFTER this
    // session's fresh auth fetch settles — otherwise we'd apply on rehydrated
    // persist state and initializeAuth would clobber the country right after.
    if (!isAuthenticated || !userDataFetchedThisSession || !(availableWorkspaces?.length))
      return;
    emailDeepLinkHandledRef.current = true;

    (async () => {
      // Only act if the user is actually a member of the email's workspace.
      const workspace = availableWorkspaces.find(
        (ws) => ws?.key === emailDeepLink.idClient
      );
      let applied = false;
      if (workspace) {
        try {
          // Switch workspace AND set the email's country atomically. The thunk
          // resolves the target against the workspace's markets (falling back to
          // its first market if the user isn't entitled to the email's country),
          // so there's no clear -> first-country -> override race.
          await dispatch(
            switchWorkspaceAndUpdateCountry({
              workspace,
              product_name: "market_overview",
              targetCountry: emailDeepLink.country,
            })
          ).unwrap();
          applied = true;
          // Signal Market Overview to seed its Region/Market selection to the
          // email's country (it's region-based, not driven by currentCountry).
          dispatch(setDeepLinkCountry(emailDeepLink.country));
        } catch {
          // switch failed — leave the user on the default welcome view
        }
      }

      // Drop the params so a refresh / back-nav doesn't re-trigger the switch.
      window.history.replaceState(
        {},
        "",
        window.location.pathname + window.location.hash
      );

      // Land on a data view — the CTA promises "View <country> data" — when the
      // switch succeeded and the user can access Market Overview; otherwise leave
      // them on the default welcome page.
      if (applied && canAccess("market_overview")) {
        navigate("/market_overview", { replace: true });
      }
    })();
  }, [
    emailDeepLink,
    isAuthenticated,
    userDataFetchedThisSession,
    availableWorkspaces,
    dispatch,
    canAccess,
    navigate,
  ]);

  // Fetch platform name + color mapping from the API once authenticated.
  //
  // localStorage cache strategy: `platformMapping.js` deliberately ships
  // no hardcoded seed, so a client with bad internet never sees platforms
  // they don't actually have. A failed `/platforms/metadata`
  // fetch with no cache means the UI renders raw `id_platform` codes
  // until the next successful fetch. Reading a cached payload first
  // keeps any prior session's data alive while the fresh response
  // loads, and on fetch failure the cached entries stay in memory
  // rather than being cleared.
  const platformMetadataFetchedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || platformMetadataFetchedRef.current) return;
    platformMetadataFetchedRef.current = true;

    const cached = readPlatformMetadataCache();
    if (cached) {
      applyPlatformMetadata(cached);
    }

    (async () => {
      try {
        const payload = await fetchPlatformMetadata();
        applyPlatformMetadata(payload);
        writePlatformMetadataCache(payload);
      } catch (err) {
        console.warn("Failed to fetch platform metadata:", err);
      }
    })();
  }, [isAuthenticated]);

  // Initialize auth after state is known
  useEffect(() => {
    const init = async () => {
      try {
        await dispatch(initializeAuth()).unwrap();
      } catch (error) {
        console.error("❌ Auth initialization failed:", error);
      }
    };

    if (!isInitialized) {
      init();
    }
  }, [dispatch, isInitialized]);

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: "#0E49B5" },
        components: {
          Segmented: {
            itemActiveBg: "rgba(255, 255, 255, 0.15)",
            trackBg: "transparent",
            itemColor: "#fff",
            itemHoverBg: "rgba(255, 255, 255, 0.15)",
            itemSelectedBg: "rgba(255, 255, 255, 0.15)",
            itemSelectedColor: "#fff",
            itemHoverColor: "#fff",
            trackPadding: "0.5rem",
          },
          Input: {
            activeBg: "trasparent",
            hoverBg: "transparent",
          },
          Table: {
            headerBg: "#fafafa",
            headerColor: "#27272a",
          },
          Tabs: {
            cardPadding: 0,
            itemActiveColor: "#fff",
            itemSelectedColor: "#fff",
            itemHoverColor: "#000",
            cardGutter: 0,
          },
          Tree: {
            indentSize: 0,
          },
        },
      }}
    >
      <AntdApp component={false}>
        <MaintenanceBanner />
        <UpdateAvailableNotification />
        <NavigationBlockingProvider>
          <AuthProvider>
              <GlobalCommandPalette />
              {isAuthenticated &&
                userDataFetchedThisSession &&
                !userData?.force_password_change && <FeedbackWidget />}
              <Routes>
                <Route
                  path="/login"
                  element={<LoginPage />}
                />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route
                  path="/reset-password"
                  element={<AcceptInvitePage mode="reset" />}
                />
                <Route
                  path="/accept-invite"
                  element={<AcceptInvitePage mode="invite" />}
                />
                <Route
                  path="/join/:token"
                  element={<JoinPage />}
                />
                <Route
                  path="/invitation-expired"
                  element={<InvitationExpiredPage />}
                />
                <Route
                  path="/invitation-invalid"
                  element={<InvitationInvalidPage />}
                />
                {/* Public DashmoteAI routes (no auth required) — shared links */}
                <Route
                  path="/ai/insights/s/:token"
                  element={
                    <Suspense fallback={
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        Loading...
                      </div>
                    }>
                      <PublicDashAIRoutes />
                    </Suspense>
                  }
                />
                <Route
                  path="/ai/canvas/s/:token"
                  element={
                    <Suspense fallback={
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        Loading...
                      </div>
                    }>
                      <PublicDashAIRoutes />
                    </Suspense>
                  }
                />
                {/* Backwards-compatible routes without /ai prefix for existing shared URLs */}
                <Route
                  path="/insights/s/:token"
                  element={
                    <Suspense fallback={
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        Loading...
                      </div>
                    }>
                      <PublicDashAIRoutes />
                    </Suspense>
                  }
                />
                <Route
                  path="/canvas/s/:token"
                  element={
                    <Suspense fallback={
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        Loading...
                      </div>
                    }>
                      <PublicDashAIRoutes />
                    </Suspense>
                  }
                />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Routes>
                        {/* Authenticated force-change screen — rendered
                            standalone (no GeneralLayout). ProtectedRoute Gate 4
                            redirects flagged users here until they set a new
                            password. */}
                        <Route
                          path="/force-password-change"
                          element={<ForcePasswordChangePage />}
                        />
                        {/* Persona onboarding wizard — standalone (no
                            GeneralLayout), always reachable directly. The
                            redirect INTO it is ProtectedRoute's Gate 5,
                            firing on the absence of a stored persona config. */}
                        <Route
                          path="/onboarding"
                          element={
                            <Suspense fallback={pageSuspenseFallback}>
                              <OnboardingPage />
                            </Suspense>
                          }
                        />
                        <Route
                          index
                          path="/"
                          element={
                            <Suspense fallback={
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                                Loading DashONE...
                              </div>
                            }>
                              <DashAIRoutes />
                            </Suspense>
                          }
                        />
                        {/* The dashboard pages share one GeneralLayout, so the
                            navigation shell stays mounted while the user moves
                            between them. */}
                        <Route element={<GeneralLayout />}>
                          <Route
                            path="/execution_view"
                            element={
                              <ModuleProtectedRoute module="execution_view">
                                <Suspense fallback={pageSuspenseFallback}>
                                  <HomePage />
                                </Suspense>
                              </ModuleProtectedRoute>
                            }
                          />
                          <Route
                            path="/outlet/:outletid"
                            element={
                              <Suspense fallback={pageSuspenseFallback}>
                                <OutletPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/market_overview"
                            element={
                              <ModuleProtectedRoute module="market_overview">
                                <Suspense fallback={pageSuspenseFallback}>
                                  <MarketOverviewPage />
                                </Suspense>
                              </ModuleProtectedRoute>
                            }
                          />
                          <Route
                            path="/market_deepdive"
                            element={
                              <ModuleProtectedRoute module="market_deepdive">
                                <Suspense fallback={pageSuspenseFallback}>
                                  <MarketDeepdivePage />
                                </Suspense>
                              </ModuleProtectedRoute>
                            }
                          />
                          <Route
                            path="/physical_location"
                            element={
                              <ModuleProtectedRoute module="physical_location">
                                <Suspense fallback={pageSuspenseFallback}>
                                  <PhysicalLocationPage />
                                </Suspense>
                              </ModuleProtectedRoute>
                            }
                          />
                          <Route
                            path="/physical_franchise"
                            element={
                              <ModuleProtectedRoute module="physical_franchise">
                                <Suspense fallback={pageSuspenseFallback}>
                                  <PhysicalFranchisePage />
                                </Suspense>
                              </ModuleProtectedRoute>
                            }
                          />
                          {/* The /accounts route (Accounts prototype) is deliberately absent on
                              stg — AccountsPage only exists on dev. The same gating applies
                              to /key_accounts and /global_key_accounts. */}
                          <Route
                            path="/accounts"
                            element={
                              /* Gated on the per-workspace workflow-visibility registry
                                 (id "accounts"), disabled by default — the Accounts
                                 prototype is hidden everywhere unless an admin enables
                                 it for a workspace. */
                              <WorkflowVisibilityRoute workflowId={ACCOUNTS_WORKFLOW_ID}>
                                <Suspense fallback={pageSuspenseFallback}>
                                  <AccountsPage />
                                </Suspense>
                              </WorkflowVisibilityRoute>
                            }
                          />
                          <Route
                            path="/key_accounts"
                            element={
                              <WorkflowVisibilityRoute workflowId={KEY_ACCOUNTS_WORKFLOW_ID}>
                                <Suspense fallback={pageSuspenseFallback}>
                                  <KeyAccountsEntry />
                                </Suspense>
                              </WorkflowVisibilityRoute>
                            }
                          />
                          <Route
                            path="/global_key_accounts"
                            element={
                              <GlobalKeyAccountsRoute>
                                <Suspense fallback={pageSuspenseFallback}>
                                  <GlobalKeyAccountsPage />
                                </Suspense>
                              </GlobalKeyAccountsRoute>
                            }
                          />
                          <Route
                            path="/my_list"
                            element={
                              <Suspense fallback={pageSuspenseFallback}>
                                <MyListPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/my_list/:listId"
                            element={
                              <Suspense fallback={pageSuspenseFallback}>
                                <MyListDetails />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/user_management"
                            element={
                              <UserManagementProtectedRoute>
                                <UserManagementPage />
                              </UserManagementProtectedRoute>
                            }
                          />
                          <Route path="/glossary" element={<Navigate to="/learn?tab=glossary" replace />} />
                          <Route path="/video_tutorial" element={<Navigate to="/learn?tab=tutorials" replace />} />
                          <Route path="/learn" element={<LearnHubPage />} />
                          {/* Catch-all 404 — any authenticated path that matches
                              no other route. It renders inside GeneralLayout so the
                              user keeps the navigation and can move away instead of
                              landing on a blank shell. */}
                          <Route path="*" element={<NotFound />} />
                        </Route>
                        <Route
                          path="/settings"
                          element={
                            <UserManagementProtectedRoute>
                              <SettingsPage />
                            </UserManagementProtectedRoute>
                          }
                        />
                        {/* DashAI shortcut routes — bare paths redirect to /ai/* */}
                        <Route path="/automated-insights" element={<RedirectAutomatedInsights />} />
                        <Route path="/automated-insights/:insightId" element={<RedirectAutomatedInsights />} />
                        <Route path="/automated-reporting" element={<RedirectAutomatedReporting />} />
                        <Route path="/automated-reporting/:reportId" element={<RedirectAutomatedReporting />} />
                        <Route path="/workflow-agents" element={<RedirectWorkflowAgents />} />
                        <Route path="/workflow-agents/:agentId" element={<RedirectWorkflowAgents />} />
                        <Route path="/saved-canvas" element={<RedirectSavedCanvas />} />
                        <Route path="/saved-canvas/:tab" element={<RedirectSavedCanvas />} />
                        <Route path="/chat/:chatId" element={<RedirectChat />} />
                        <Route path="/chat/:chatId/:canvasId" element={<RedirectChat />} />
                        {/* DashmoteAI routes — /ai/* */}
                        <Route
                          path="/ai/*"
                          element={
                            <Suspense fallback={
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                                Loading DashONE...
                              </div>
                            }>
                              <DashAIRoutes />
                            </Suspense>
                          }
                        />
                      </Routes>
                    </ProtectedRoute>
                  }
                />
              </Routes>
          </AuthProvider>
        </NavigationBlockingProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
